"use strict";
/**
 * ConfigService — loads service configuration from database at startup.
 *
 * Replaces process.env access with typed getters.
 * Config is loaded once into memory; call reload() to refresh.
 *
 * @example
 * ```typescript
 * const config = await ConfigService.create({
 *   pool,
 *   requiredKeys: ['redis.url', 'rabbitmq.url', 'jwt.algorithm'],
 * });
 *
 * const redisUrl = config.getRequired('redis.url');
 * const maxRetries = config.getInt('worker.max_retries', 3);
 * const enabled = config.getBool('feature.search_enabled', false);
 * ```
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigService = exports.ConfigError = void 0;
class ConfigError extends Error {
    constructor(message, missingKeys = []) {
        super(message);
        this.name = 'ConfigError';
        this.missingKeys = missingKeys;
    }
}
exports.ConfigError = ConfigError;
class ConfigService {
    constructor(options) {
        this.store = new Map();
        this.pool = options.pool;
        this.tableName = options.tableName ?? 'service_config';
        this.requiredKeys = options.requiredKeys ?? [];
    }
    /**
     * Create and initialize a ConfigService.
     * Loads all rows from the config table and validates required keys.
     */
    static async create(options) {
        const service = new ConfigService(options);
        await service.load();
        service.validateRequired();
        return service;
    }
    /** Get a string value. Returns defaultValue (or undefined) if key is missing. */
    get(key, defaultValue) {
        return this.store.get(key) ?? defaultValue;
    }
    /** Get a string value. Throws ConfigError if key is missing. */
    getRequired(key) {
        const value = this.store.get(key);
        if (value === undefined) {
            throw new ConfigError(`Required config key missing: '${key}'`, [key]);
        }
        return value;
    }
    /** Get an integer value. Returns defaultValue if key is missing or not a valid integer. */
    getInt(key, defaultValue) {
        const raw = this.store.get(key);
        if (raw === undefined) {
            if (defaultValue !== undefined)
                return defaultValue;
            throw new ConfigError(`Config key missing: '${key}' (expected integer)`, [key]);
        }
        const parsed = parseInt(raw, 10);
        if (isNaN(parsed)) {
            if (defaultValue !== undefined)
                return defaultValue;
            throw new ConfigError(`Config key '${key}' is not a valid integer: '${raw}'`);
        }
        return parsed;
    }
    /** Get a boolean value. Truthy: 'true', '1', 'yes'. Everything else is false. */
    getBool(key, defaultValue) {
        const raw = this.store.get(key);
        if (raw === undefined) {
            if (defaultValue !== undefined)
                return defaultValue;
            throw new ConfigError(`Config key missing: '${key}' (expected boolean)`, [key]);
        }
        return ['true', '1', 'yes'].includes(raw.toLowerCase());
    }
    /** Get a JSON-parsed value. Throws ConfigError if key is missing or JSON is invalid. */
    getJson(key) {
        const raw = this.store.get(key);
        if (raw === undefined) {
            throw new ConfigError(`Config key missing: '${key}' (expected JSON)`, [key]);
        }
        try {
            return JSON.parse(raw);
        }
        catch {
            throw new ConfigError(`Config key '${key}' is not valid JSON: '${raw}'`);
        }
    }
    /** Get all keys matching a category prefix (e.g., 'redis.' returns all redis.* keys). */
    getByPrefix(prefix) {
        const result = {};
        for (const [key, value] of this.store) {
            if (key.startsWith(prefix)) {
                result[key] = value;
            }
        }
        return result;
    }
    /** Check if a key exists in the config. */
    has(key) {
        return this.store.has(key);
    }
    /** Number of loaded config keys. */
    get size() {
        return this.store.size;
    }
    /** Reload config from database. */
    async reload() {
        await this.load();
        this.validateRequired();
    }
    /** Load all rows from the config table into memory. */
    async load() {
        const result = await this.pool.query(`SELECT key, value FROM ${this.tableName}`);
        this.store = new Map(result.rows.map((row) => [row.key, row.value]));
    }
    /** Validate that all required keys are present. */
    validateRequired() {
        if (this.requiredKeys.length === 0)
            return;
        const missing = this.requiredKeys.filter((key) => !this.store.has(key));
        if (missing.length > 0) {
            throw new ConfigError(`Missing required config keys: ${missing.join(', ')}`, missing);
        }
    }
}
exports.ConfigService = ConfigService;
